(() => {
  if (window.ABCColorCards) return;
  const manifests = new Map();
  const cards = new WeakMap();
  const photographs = new Map();
  const key = (value) => String(value || '').trim().toLocaleLowerCase();
  const colorOrder = ['black', 'faded black', 'flower gray', 'light gray', 'white', 'mild apricot', 'light apricot', 'navy blue', 'royal blue', 'blackish green', 'wine red'];
  const colorRank = (name) => {
    const index = colorOrder.indexOf(key(name));
    return index < 0 ? colorOrder.length : index;
  };
  const compareColors = (left, right) => colorRank(left) - colorRank(right) || key(left).localeCompare(key(right), 'en');
  const clamp = (value, maximum) => Math.max(0, Math.min(value, maximum));
  const photoUrl = (value, width = 900) => {
    try {
      const url = new URL(value, location.href);
      if (!value || !['http:', 'https:'].includes(url.protocol)) return '';
      if (url.hostname === 'cdn.shopify.com' || (url.origin === location.origin && url.pathname.startsWith('/cdn/shop/'))) url.searchParams.set('width', String(width));
      return url.href;
    } catch { return ''; }
  };
  const productUrl = (value, base) => {
    try {
      const url = new URL(value, location.href);
      const original = new URL(base, location.href);
      if (!value || !base || url.origin !== location.origin || !/^\/(?:[a-z]{2}(?:-[A-Z]{2})?\/)?products\//.test(url.pathname)) return '';
      const handle = original.pathname.split('/products/')[1];
      if (!handle || url.pathname.split('/products/')[1] !== handle) return '';
      url.pathname = original.pathname;
      return url.pathname + url.search + url.hash;
    } catch { return ''; }
  };
  const gestureStep = (gesture, width) => {
    const dx = gesture.endX - gesture.startX;
    const dy = gesture.endY - gesture.startY;
    if (gesture.cancelled || Math.abs(dx) < Math.max(35, Math.min(80, width * .1)) || Math.abs(dx) < Math.abs(dy) * 1.4) return 0;
    return dx < 0 ? 1 : -1;
  };
  const fetchManifest = (url) => {
    if (!manifests.has(url)) {
      const abort = new AbortController();
      const timeout = setTimeout(() => abort.abort(), 8000);
      manifests.set(url, fetch(url, { signal: abort.signal, credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error('Media unavailable');
          return response.json();
        }).catch(() => null).finally(() => clearTimeout(timeout)));
    }
    return manifests.get(url);
  };
  const loadPhoto = (src, priority = 'low') => {
    if (!src) return Promise.resolve(false);
    if (photographs.has(src)) {
      const existing = photographs.get(src);
      if (priority === 'high' && existing.image) existing.image.fetchPriority = 'high';
      return existing.promise;
    }
    const record = { state: 'pending', promise: null };
    photographs.set(src, record);
    record.promise = new Promise((resolve) => {
      const img = new Image();
      record.image = img;
      let settled = false;
      const finish = (loaded) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        record.state = loaded ? 'loaded' : 'failed';
        if (!loaded) photographs.delete(src);
        resolve(loaded);
      };
      const timeout = setTimeout(() => finish(false), 12000);
      img.onload = () => {
        if (typeof img.decode === 'function') img.decode().catch(() => {}).then(() => finish(true));
        else finish(true);
      };
      img.onerror = () => finish(false);
      img.decoding = 'async';
      img.fetchPriority = priority;
      img.src = src;
    });
    return record.promise;
  };
  const image = (src, alt, look = false, eager = false) => {
    const img = document.createElement('img');
    img.src = src;
    img.alt = alt;
    img.loading = eager ? 'eager' : 'lazy';
    img.decoding = 'async';
    img.draggable = false;
    img.width = 1200;
    img.height = 1200;
    if (look) img.dataset.colorLook = '';
    img.addEventListener('load', () => {
      if (look && img.naturalWidth > img.naturalHeight) img.closest('.abc-mobile-card-gallery__slide')?.classList.add('abc-mobile-card-gallery__slide--wide');
    });
    img.addEventListener('error', () => { img.style.visibility = 'hidden'; });
    return img;
  };
  const normalizeEntry = (entry, base) => {
    const front = photoUrl(entry?.front);
    const back = photoUrl(entry?.back);
    const pair = typeof entry?.pair === 'string' ? photoUrl(entry.pair, 1600) : '';
    return {
      name: String(entry?.name || '').trim(), front, back, pair,
      splitPair: entry?.pair === true && !back,
      looks: (Array.isArray(entry?.looks) ? entry.looks : []).slice(0, 2).map((look) => ({ src: photoUrl(look?.src), alt: String(look?.alt || ''), width: Number(look?.width) || 0, height: Number(look?.height) || 0 })).filter((look) => look.src),
      url: productUrl(entry?.url, base),
    };
  };
  class ColorCard {
    constructor(card) {
      this.card = card;
      this.visual = card.querySelector('.abc-store-card__visual');
      if (!this.visual) return;
      this.buttons = [...card.querySelectorAll('[data-card-color]')];
      this.titleLink = card.querySelector('.abc-store-card__copy h3 a');
      this.baseUrl = card.dataset.cardUrl || '';
      this.url = this.baseUrl;
      this.current = 0;
      this.interacted = false;
      this.renderVersion = 0;
      this.nearViewport = false;
      this.entries = this.fallbackEntries();
      this.displayedEntry = this.entries[0];
      this.reorderColors(this.displayedEntry?.name);
      this.events = new AbortController();
      this.desktop = matchMedia('(min-width: 750px)');
      this.blockOpenUntil = 0;
      this.onResize = () => {
        this.cancelGesture();
        if (this.nearViewport || this.interacted) this.render(false);
      };
      this.desktop.addEventListener('change', this.onResize);
      this.bind();
      this.updateButtons();
      this.observe();
      this.load();
    }
    fallbackEntries() {
      const productImages = [...this.visual.querySelectorAll('.abc-store-card__product img')];
      const first = productImages[0];
      const looks = [...this.visual.querySelectorAll('.abc-store-card__look img')].map((img) => ({ src: img.currentSrc || img.src, alt: img.alt }));
      return this.buttons.map((button, index) => normalizeEntry({
        name: button.dataset.cardColor,
        front: index === 0 ? first?.currentSrc || first?.src : button.dataset.colorFront,
        back: index === 0 ? productImages[1]?.currentSrc || productImages[1]?.src : '',
        looks: index === 0 ? looks : [],
        pair: index === 0 && !productImages[1] && (first?.src || '').includes('top-front-back'),
        url: button.dataset.colorUrl || this.baseUrl,
      }, this.baseUrl));
    }
    reorderColors(selectedName) {
      this.buttons.sort((left, right) => compareColors(left.dataset.cardColor, right.dataset.cardColor));
      for (const button of this.buttons) {
        const item = button.closest('[role="listitem"]');
        item?.parentElement?.append(item);
      }
      this.entries.sort((left, right) => compareColors(left.name, right.name));
      const selectedIndex = this.entries.findIndex((entry) => key(entry.name) === key(selectedName));
      this.current = selectedIndex >= 0 ? selectedIndex : 0;
    }
    async load() {
      const result = await fetchManifest(this.card.dataset.cardManifest);
      if (this.events.signal.aborted) return;
      const raw = (result?.products || result)?.[this.card.dataset.productHandle];
      if (!Array.isArray(raw)) return;
      const normalized = raw.map((entry) => normalizeEntry(entry, this.baseUrl)).filter((entry) => entry.name && (entry.front || entry.pair));
      if (!normalized.length) return;
      const previousName = this.entries[this.current]?.name;
      const merged = normalized.filter((entry) => this.buttons.some((button) => key(button.dataset.cardColor) === key(entry.name)));
      for (const fallback of this.entries) {
        if (!merged.some((entry) => key(entry.name) === key(fallback.name))) merged.push(fallback);
      }
      const selectedName = this.interacted ? previousName : merged[0]?.name;
      this.entries = merged;
      this.reorderColors(selectedName);
      this.catalogLoaded = true;
      this.updateSelection(false);
      if (this.nearViewport) { this.preloadAll(); this.render(false); }
    }
    observe() {
      if (!('IntersectionObserver' in window)) {
        this.nearViewport = true;
        return;
      }
      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.nearViewport = true;
        this.observer.disconnect();
        if (this.catalogLoaded) { this.preloadAll(); this.render(false); }
      }, { rootMargin: '500px 0px', threshold: 0 });
      this.observer.observe(this.card);
    }
    preloadAll() {
      this.entries.forEach((entry) => [entry.pair, entry.front, entry.back].filter(Boolean).forEach((src) => loadPhoto(src)));
    }
    bind() {
      const options = { signal: this.events.signal };
      this.card.addEventListener('load', (event) => {
        const img = event.target;
        if (img?.tagName !== 'IMG' || !('colorLook' in img.dataset)) return;
        img.closest('.abc-mobile-card-gallery__slide')?.classList.toggle('abc-mobile-card-gallery__slide--wide', img.naturalWidth > img.naturalHeight);
      }, { ...options, capture: true });
      this.card.addEventListener('error', (event) => {
        if (event.target?.tagName === 'IMG') {
          event.target.style.visibility = 'hidden';
          if (event.target.closest?.('.abc-store-card__look')) this.visual.classList.toggle('has-look', false);
        }
      }, { ...options, capture: true });
      this.visual.setAttribute('role', this.baseUrl ? 'link' : 'group');
      this.visual.setAttribute('tabindex', '0');
      this.visual.setAttribute('aria-label', this.card.dataset.cardTitle);
      this.visual.setAttribute('aria-description', this.card.dataset.keyboardHint);
      this.visual.addEventListener('dragstart', (event) => event.preventDefault(), options);
      this.visual.addEventListener('click', (event) => {
        if (!this.url || (event.detail !== 0 && performance.now() < this.blockOpenUntil)) event.preventDefault();
      }, options);
      this.visual.addEventListener('keydown', (event) => {
        if (!this.desktop.matches) return;
        if (event.key === 'Enter') { event.preventDefault(); this.open(); return; }
        const next = { ArrowLeft: this.current - 1, ArrowRight: this.current + 1, Home: 0, End: this.entries.length - 1 };
        if (!(event.key in next)) return;
        event.preventDefault();
        this.select(clamp(next[event.key], this.entries.length - 1));
      }, options);
      this.visual.addEventListener('pointerdown', (event) => {
        if (!this.desktop.matches || event.button !== 0 || event.pointerType === 'touch') return;
        this.finishSlide?.();
        this.blockOpenUntil = 0;
        if (this.entries.length < 2) return;
        this.gesture = { id: event.pointerId, startX: event.clientX, startY: event.clientY, endX: event.clientX, endY: event.clientY, cancelled: false, dragging: false };
        [this.entries[this.current - 1], this.entries[this.current + 1]].filter(Boolean).forEach((entry) => this.preload(entry.name));
      }, options);
      this.visual.addEventListener('pointermove', (event) => {
        const gesture = this.gesture;
        if (!gesture || event.pointerId !== gesture.id) return;
        gesture.endX = event.clientX;
        gesture.endY = event.clientY;
        const dx = Math.abs(gesture.endX - gesture.startX);
        const dy = Math.abs(gesture.endY - gesture.startY);
        if (!gesture.dragging && dy > 12 && dy > dx) { this.cancelGesture(); return; }
        if (!gesture.dragging && dx > 8 && dx > dy * 1.4) {
          gesture.dragging = true;
          this.visual.setPointerCapture?.(event.pointerId);
          this.card.dataset.colorDragging = 'true';
          this.card.dataset.colorSuppressLook = 'true';
          this.startSlide();
        }
        if (gesture.dragging) {
          event.preventDefault();
          this.dragOffset = gesture.endX - gesture.startX;
          if (!this.dragFrame) this.dragFrame = requestAnimationFrame(() => this.paintDrag());
        }
      }, options);
      this.visual.addEventListener('pointerup', (event) => {
        const gesture = this.gesture;
        if (!gesture || event.pointerId !== gesture.id) return;
        gesture.endX = event.clientX;
        gesture.endY = event.clientY;
        const step = gestureStep(gesture, this.visual.clientWidth);
        if (gesture.dragging || step) this.blockOpenUntil = performance.now() + 650;
        const next = clamp(this.current + step, this.entries.length - 1);
        const direction = next === this.current ? 0 : step;
        const dragging = gesture.dragging;
        this.releaseGesture();
        if (dragging) this.settleSlide(direction, () => { if (direction) this.select(next); });
        else if (direction) this.select(next);
      }, options);
      this.visual.addEventListener('pointercancel', () => {
        if (this.gesture?.dragging) this.blockOpenUntil = performance.now() + 650;
        this.releaseGesture();
        this.settleSlide(0);
      }, options);
      this.visual.addEventListener('lostpointercapture', () => { if (this.gesture) this.cancelGesture(); }, options);
      this.visual.addEventListener('pointerleave', () => {
        if (this.gesture && !this.gesture.dragging) this.cancelGesture();
        if (!this.gesture && !this.slide) delete this.card.dataset.colorSuppressLook;
      }, options);
      this.visual.addEventListener('pointerenter', () => {
        if (!this.gesture && !this.slide) delete this.card.dataset.colorSuppressLook;
      }, options);
      this.buttons.forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          this.select(this.entries.findIndex((entry) => key(entry.name) === key(button.dataset.cardColor)));
        }, options);
        button.addEventListener('pointerenter', () => this.preload(button.dataset.cardColor), options);
        button.addEventListener('focus', () => this.preload(button.dataset.cardColor), options);
        button.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const available = this.buttons.filter((item) => !item.disabled);
          const current = available.indexOf(button);
          const target = event.key === 'Home' ? 0 : event.key === 'End' ? available.length - 1 : clamp(current + (event.key === 'ArrowRight' ? 1 : -1), available.length - 1);
          available[target]?.focus();
          available[target]?.click();
        }, options);
      });
      this.card.dataset.colorReady = 'true';
    }
    releaseGesture() {
      const id = this.gesture?.id;
      this.gesture = null;
      delete this.card.dataset.colorDragging;
      if (id !== undefined && this.visual.hasPointerCapture?.(id)) this.visual.releasePointerCapture(id);
    }
    cancelGesture() {
      this.releaseGesture();
      this.clearSlide();
    }
    photoSources(entry) {
      return [entry?.pair || entry?.front, ...(entry?.pair ? [] : [entry?.back])].filter(Boolean);
    }
    ready(entry) {
      const sources = this.photoSources(entry);
      return sources.length > 0 && sources.every((src) => photographs.get(src)?.state === 'loaded');
    }
    startSlide() {
      this.clearSlide();
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const rail = document.createElement('div');
      rail.className = 'abc-card-color-rail';
      rail.setAttribute('aria-hidden', 'true');
      const shown = this.displayedEntry || this.entries[this.current];
      const ready = [-1, 0, 1].map((step) => this.ready(this.entries[this.current + step]));
      [-1, 0, 1].forEach((step, index) => {
        const panel = document.createElement('div');
        panel.className = 'abc-card-color-panel';
        panel.append(this.productPhoto(ready[index] ? this.entries[this.current + step] : shown));
        rail.append(panel);
      });
      this.slide = { rail, ready, width: this.visual.clientWidth };
      this.card.dataset.colorSliding = 'true';
      this.visual.append(rail);
    }
    paintDrag() {
      this.dragFrame = 0;
      if (!this.slide) return;
      const { rail, ready, width } = this.slide;
      const dx = this.dragOffset || 0;
      const direction = dx < 0 ? 1 : -1;
      const canSlide = ready[direction + 1] && this.entries[this.current + direction];
      const distance = canSlide ? Math.min(Math.abs(dx), width) : width * .18 * (1 - Math.exp(-Math.abs(dx) / (width * .5)));
      rail.style.setProperty('--abc-color-offset', `${Math.sign(dx) * distance}px`);
    }
    settleSlide(direction, after = () => {}) {
      if (!this.slide || matchMedia('(prefers-reduced-motion: reduce)').matches) { this.clearSlide(); after(); return; }
      cancelAnimationFrame(this.dragFrame);
      this.paintDrag();
      const { rail, ready, width } = this.slide;
      const target = direction && ready[direction + 1] ? -direction * width : 0;
      rail.getBoundingClientRect();
      rail.classList.add('is-settling');
      rail.style.setProperty('--abc-color-offset', `${target}px`);
      let finished = false;
      const finish = () => {
        if (finished) return;
        finished = true;
        this.clearSlide();
        after();
      };
      this.finishSlide = finish;
      rail.addEventListener('transitionend', (event) => { if (event.target === rail && event.propertyName === 'transform') finish(); }, { once: true });
      this.slideTimer = setTimeout(finish, 340);
    }
    clearSlide() {
      cancelAnimationFrame(this.dragFrame);
      this.dragFrame = 0;
      this.dragOffset = 0;
      clearTimeout(this.slideTimer);
      this.finishSlide = null;
      this.slide?.rail.remove();
      this.slide = null;
      delete this.card.dataset.colorSliding;
    }
    preload(name) {
      const entry = this.entries.find((item) => key(item.name) === key(name));
      if (!entry) return;
      [entry.pair || entry.front, entry.front, entry.back, ...entry.looks.map((look) => look.src)].filter(Boolean).forEach((src) => loadPhoto(src));
    }
    select(index, direction = 0) {
      const entry = this.entries[index];
      if (!entry || (!entry.front && !entry.pair)) return;
      this.interacted = true;
      if (index === this.current) return;
      this.cancelGesture();
      this.current = index;
      this.render(true, direction);
    }
    updateSelection(announce) {
      const entry = this.entries[this.current];
      if (!entry) return;
      this.url = entry.url || this.baseUrl;
      if (this.titleLink && this.url) this.titleLink.href = this.url;
      if (this.url) this.visual.setAttribute('href', this.url);
      else this.visual.removeAttribute('href');
      this.updateButtons();
      if (announce) this.card.querySelector('[data-card-color-status]').textContent = entry.name;
    }
    updateButtons() {
      const selected = this.entries[this.current];
      this.buttons.forEach((button) => {
        const entry = this.entries.find((item) => key(item.name) === key(button.dataset.cardColor));
        button.disabled = !entry || (!entry.front && !entry.pair);
        button.setAttribute('aria-pressed', String(key(button.dataset.cardColor) === key(selected?.name)));
      });
      this.card.dataset.colorCount = String(this.entries.length);
    }
    render(announce, direction = 0) {
      const entry = this.entries[this.current];
      if (!entry) return;
      const version = ++this.renderVersion;
      this.updateSelection(announce);
      this.card.dataset.colorLoading = 'true';
      this.visual.setAttribute('aria-busy', 'true');
      this.card.querySelector('abc-mobile-card-gallery')?.setAttribute('aria-busy', 'true');
      const sources = this.desktop.matches
        ? [entry.pair || entry.front, ...(entry.pair ? [] : [entry.back])]
        : [entry.front || entry.pair, entry.back];
      const required = [...new Set(sources.filter(Boolean))];
      if (this.desktop.matches && entry.looks.length) {
        Promise.all(entry.looks.map((look) => loadPhoto(look.src))).then((ready) => {
          if (version !== this.renderVersion || this.events.signal.aborted || this.displayedEntry !== entry) return;
          this.visual.classList.toggle('has-look', ready.every(Boolean));
        });
      }
      const commit = () => {
        if (version !== this.renderVersion || this.events.signal.aborted) return;
        delete this.card.dataset.colorLoading;
        this.visual.setAttribute('aria-busy', 'false');
        this.card.querySelector('abc-mobile-card-gallery')?.setAttribute('aria-busy', 'false');
        this.commit(entry, announce, direction);
      };
      if (required.every((src) => photographs.get(src)?.state === 'loaded')) { commit(); return; }
      const pending = required.map((src) => loadPhoto(src, 'high'));
      if (required.every((src) => photographs.get(src)?.state === 'loaded')) { commit(); return; }
      Promise.all(pending).then((ready) => {
        if (version !== this.renderVersion || this.events.signal.aborted) return;
        if (ready.every(Boolean)) { commit(); return; }
        delete this.card.dataset.colorLoading;
        this.visual.setAttribute('aria-busy', 'false');
        this.card.querySelector('abc-mobile-card-gallery')?.setAttribute('aria-busy', 'false');
        const previous = this.entries.findIndex((item) => key(item.name) === key(this.displayedEntry?.name));
        if (previous >= 0) { this.current = previous; this.updateSelection(false); }
        this.card.querySelector('[data-card-color-status]').textContent = this.card.dataset.photoLoadError || this.displayedEntry?.name || '';
      });
    }
    productPhoto(entry) {
      const title = this.card.dataset.cardTitle;
      const product = document.createElement('div');
      product.className = 'abc-store-card__product';
      if (entry.pair) {
        product.append(image(entry.pair, `${title} — ${entry.name}`, false, true));
      } else {
        if (entry.back && !entry.splitPair) product.classList.add('abc-store-card__pair');
        product.append(image(entry.front, `${title} — ${entry.name} — ${this.card.dataset.frontLabel}`, false, true));
        if (entry.back) product.append(image(entry.back, `${title} — ${entry.name} — ${this.card.dataset.backLabel}`, false, true));
      }
      return product;
    }
    commit(entry, announce, direction) {
      this.cancelGesture();
      const fragments = [this.productPhoto(entry)];
      if (entry.looks.length) {
        const look = document.createElement('div');
        look.className = 'abc-store-card__look' + (entry.looks.length > 1 ? ' abc-store-card__pair' : '');
        look.setAttribute('aria-hidden', 'true');
        entry.looks.forEach((photo) => look.append(image(photo.src, '', true)));
        fragments.push(look);
      }
      this.visual.replaceChildren(...fragments);
      this.visual.classList.toggle('has-look', entry.looks.length > 0 && entry.looks.every((look) => photographs.get(look.src)?.state === 'loaded'));
      this.displayedEntry = entry;
      this.renderMobile(entry);
    }
    renderMobile(entry) {
      let gallery = this.card.querySelector('abc-mobile-card-gallery');
      if (!gallery) {
        gallery = document.createElement('abc-mobile-card-gallery');
        this.visual.after(gallery);
      }
      gallery.unmount?.();
      const template = document.createElement('template');
      const body = document.createElement('div');
      body.className = 'abc-mobile-card-gallery__body';
      const track = document.createElement('div');
      track.className = 'abc-mobile-card-gallery__track';
      track.setAttribute('tabindex', '0');
      track.setAttribute('role', 'region');
      track.setAttribute('aria-label', `${this.card.dataset.cardTitle} — ${entry.name} — ${this.card.dataset.galleryLabel}`);
      const pairOnly = entry.splitPair || (!entry.front && entry.pair);
      const photos = pairOnly ? [
        { src: entry.front || entry.pair, view: this.card.dataset.frontLabel, half: 'front' },
        { src: entry.front || entry.pair, view: this.card.dataset.backLabel, half: 'back' },
      ] : [
        { src: entry.front, view: this.card.dataset.frontLabel },
        ...(entry.back ? [{ src: entry.back, view: this.card.dataset.backLabel }] : []),
      ];
      entry.looks.forEach((look) => photos.push({ src: look.src, view: this.card.dataset.lookLabel, alt: look.alt, width: look.width, height: look.height, look: true }));
      photos.forEach((photo, index) => {
        if (!photo.src) return;
        const href = productUrl(entry.url || this.baseUrl, this.baseUrl);
        const slide = document.createElement(href ? 'a' : 'div');
        if (href) slide.href = href;
        slide.className = 'abc-mobile-card-gallery__slide' + (photo.look ? ' abc-mobile-card-gallery__slide--look' : '') + (photo.look && photo.width > photo.height ? ' abc-mobile-card-gallery__slide--wide' : '') + (photo.half ? ` abc-card-color-half abc-card-color-half--${photo.half}` : '');
        const alt = photo.alt || `${this.card.dataset.cardTitle} — ${entry.name} — ${photo.view}`;
        if (!href) slide.setAttribute('role', 'group');
        slide.setAttribute('aria-label', `${index + 1} — ${alt}`);
        slide.append(image(photo.src, alt, photo.look, !photo.look));
        track.append(slide);
      });
      body.append(track);
      if (photos.length > 1) {
        const controls = document.createElement('div');
        controls.className = 'abc-mobile-card-gallery__controls';
        [-1, 1].forEach((step) => {
          if (step === 1) {
            const counter = document.createElement('span');
            counter.className = 'abc-mobile-card-gallery__counter';
            counter.setAttribute('aria-live', 'polite');
            counter.setAttribute('aria-atomic', 'true');
            const current = document.createElement('span');
            current.dataset.galleryCurrent = '';
            current.textContent = '1';
            counter.append(current, ` / ${photos.length}`);
            controls.append(counter);
          }
          const button = document.createElement('button');
          button.type = 'button';
          button.dataset.galleryStep = String(step);
          button.setAttribute('aria-label', step === -1 ? this.card.dataset.previousLabel : this.card.dataset.nextLabel);
          button.disabled = step === -1;
          const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
          svg.setAttribute('viewBox', '0 0 24 24');
          svg.setAttribute('width', '18');
          svg.setAttribute('height', '18');
          svg.setAttribute('fill', 'none');
          svg.setAttribute('aria-hidden', 'true');
          const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          path.setAttribute('d', step === -1 ? 'm14 6-6 6 6 6' : 'm10 6 6 6-6 6');
          path.setAttribute('stroke', 'currentColor');
          path.setAttribute('stroke-width', '1.4');
          svg.append(path);
          button.append(svg);
          controls.append(button);
        });
        body.append(controls);
      }
      template.content.append(body);
      gallery.replaceChildren(template);
      if (gallery.media?.matches) gallery.mount();
    }
    open() {
      const safe = productUrl(this.url, this.baseUrl);
      if (safe) location.assign(safe);
    }
    destroy() {
      this.renderVersion++;
      this.observer?.disconnect();
      this.cancelGesture();
      this.events.abort();
      this.desktop.removeEventListener('change', this.onResize);
    }
  }
  const init = (root = document) => root.querySelectorAll('[data-card-colors]').forEach((card) => {
    if (!cards.has(card)) cards.set(card, new ColorCard(card));
  });
  window.ABCColorCards = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => event.target.querySelectorAll('[data-card-colors]').forEach((card) => {
    cards.get(card)?.destroy();
    cards.delete(card);
  }));
})();
